import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  AUTH_STORAGE_KEYS,
  clearAuthStorage,
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
} from "api/client";
import {
  AdminUser,
  AuthTokens,
  isAdminRole,
  loginRequest,
  LoginRequest,
  logoutRequest,
} from "api/auth";

interface AuthContextValue {
  user: AdminUser | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isInitializing: boolean;
  login: (credentials: LoginRequest) => Promise<AdminUser>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function readStoredUser(): AdminUser | null {
  const raw = localStorage.getItem(AUTH_STORAGE_KEYS.USER);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AdminUser;
  } catch {
    return null;
  }
}

function persistUser(user: AdminUser): void {
  localStorage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(user));
}

function persistTokens(tokens: AuthTokens): void {
  setAccessToken(tokens.access.token);
  setRefreshToken(tokens.refresh.token);
}

export function AuthProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);

  useEffect(() => {
    const token = getAccessToken();
    const storedUser = readStoredUser();
    if (token && storedUser && isAdminRole(storedUser.role)) {
      setUser(storedUser);
    } else if (token && storedUser && !isAdminRole(storedUser.role)) {
      // Non-admin token in storage — clear it.
      clearAuthStorage();
    }
    setIsInitializing(false);
  }, []);

  const login = useCallback(async (credentials: LoginRequest): Promise<AdminUser> => {
    const response = await loginRequest(credentials);
    if (!isAdminRole(response.user.role)) {
      throw new Error("This account does not have admin access.");
    }
    persistTokens(response.tokens);
    persistUser(response.user);
    setUser(response.user);
    return response.user;
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      try {
        await logoutRequest(refreshToken);
      } catch {
        // Ignore server errors during logout; local state must still clear.
      }
    }
    clearAuthStorage();
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isAdmin: user !== null && isAdminRole(user.role),
      isInitializing,
      login,
      logout,
    }),
    [user, isInitializing, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
